// "This device has no voice for <language>" hint.
//
// Android's Google TTS only has the voice packs that have been DOWNLOADED, so an English phone
// commonly has no German voice at all. The readout used to paper over that by letting the engine
// pick its default voice, which read German words with English phonetics — actively teaching the
// wrong pronunciation in a reading app. speakRulePrompt now stays silent instead (src/game/audio.ts),
// and this card explains the silence and how to fix it.
//
// ANDROID ONLY, deliberately. On iOS the compact voices for every system-supported language ship
// with the OS rather than being downloaded, so the case is not expected to arise there; showing an
// iPhone user an instruction for a problem they don't have — or worse, a path that doesn't apply —
// is worse than the silence itself. The platform switch below is where a second branch goes if a
// real iOS gap ever turns up.
import i18next from 'i18next';
import * as overlay from './overlay';
import { platform } from '@/platform';
import { hasVoiceForLang } from '@/game/audio';
import { getVoiceHintShown, setVoiceHintShown } from '@/game/storage';
import { LANG_NATIVE, type Lang } from '@/i18n';
import { devFlag } from '@/game/dev';

let popHandler: ((e: PopStateEvent) => void) | null = null;

/**
 * Show the hint if — and only if — this language is genuinely mute on this device and we have not
 * said so before. Safe to call on every language switch and once at boot.
 *
 * Called from a settled moment (a language pick, or startup) rather than from the readout path:
 * Android's voice list can still be filling in during the first frames, and a hint shown off a
 * half-loaded list would be plain wrong. A momentarily-silent readout self-corrects; a false hint
 * sends the player into system settings for nothing.
 */
export function maybeShowVoiceHint(lang: string): void {
  // Never clobber another overlay (privacy, a picker, the buy prompt) — they share one #overlay
  // element, so showing on top would silently replace whatever the player is looking at. Skipping
  // costs nothing: the check runs again on the next menu visit.
  if (document.getElementById('overlay')?.style.display === 'block') return;
  // `?voicehint` forces the card so it can be reviewed on a desktop browser, where the real
  // trigger (Android + a genuinely missing voice) can't be reproduced. Inert on production.
  if (devFlag('voicehint')) { openVoiceHint(lang); return; }
  if (platform().platform !== 'android') return;
  if (getVoiceHintShown(lang)) return;
  if (hasVoiceForLang(lang)) return;
  setVoiceHintShown(lang);
  openVoiceHint(lang);
}

/**
 * Boot-time check for the language already persisted from a previous session — an existing install
 * sitting on a mute language would otherwise only ever be told if the player happened to switch
 * away and back. Once per app session, because it is a startup condition and not a per-menu one.
 *
 * Runs from the menu rather than main.ts: the voice list is populated after the first user gesture
 * (primeSpeech in src/game/audio.ts), which has not happened yet at boot. If it is still empty here
 * hasVoiceForLang answers "true" and we simply say nothing — the next menu visit retries.
 */
let bootChecked = false;

export function checkVoiceHintOnce(lang: string): void {
  if (bootChecked) return;
  bootChecked = true;
  maybeShowVoiceHint(lang);
}

export function openVoiceHint(lang: string): void {
  // The language's own name ("Deutsch"), not a translated one: the card renders in the language the
  // player just chose, so its endonym is the label they were looking at when they chose it.
  const name = LANG_NATIVE[lang as Lang] ?? lang;
  const title = i18next.t('voice.noVoiceTitle');
  const body = i18next.t('voice.noVoiceBody', { language: name });
  const how = i18next.t('voice.howToAndroid');
  const ok = i18next.t('voice.gotIt');

  overlay.show(`
    <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
                background:rgba(6,8,11,0.82);padding:24px;
                padding-top:calc(24px + env(safe-area-inset-top));
                font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;">
      <div role="dialog" aria-modal="true" aria-label="${title}"
           style="max-width:520px;width:100%;max-height:100%;overflow-y:auto;background:#1a1f25;
                  border:1px solid #3a3f47;border-radius:16px;padding:20px 22px;
                  box-shadow:0 18px 48px rgba(0,0,0,0.5);">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px;">
          <span style="font-size:26px;line-height:1;">🔇</span>
          <span style="color:#e8edf2;font-size:19px;font-weight:600;">${title}</span>
        </div>
        <p style="color:#c3ccd6;font-size:15px;line-height:1.5;margin:0 0 14px;">${body}</p>
        <p style="color:#9fb0c0;font-size:14px;line-height:1.6;margin:0 0 20px;
                  background:#141a20;border-radius:10px;padding:12px 14px;">${how}</p>
        <button id="voice-hint-ok"
                style="appearance:none;border:none;width:100%;background:#243240;color:#7ec4ff;
                       font-size:16px;font-weight:600;cursor:pointer;border-radius:10px;
                       min-height:48px;">${ok}</button>
      </div>
    </div>
  `);

  // Hardware/gesture Back closes the hint instead of leaving the app (same contract as
  // privacyOverlay — the device Back button must never drop the player out of the game).
  history.pushState({ voiceHint: true }, '');
  popHandler = () => close({ fromPop: true });
  window.addEventListener('popstate', popHandler);

  document.getElementById('voice-hint-ok')?.addEventListener('click', () => close({ fromPop: false }));
}

// fromPop: the close was triggered by a popstate (Back) — history already moved, so we must NOT
// call history.back() again. Otherwise pop our pushed entry.
function close({ fromPop }: { fromPop: boolean }): void {
  if (popHandler) {
    window.removeEventListener('popstate', popHandler);
    popHandler = null;
  }
  overlay.hide();
  if (!fromPop && history.state?.voiceHint) history.back();
}
